import type { RecommendationPanelDto } from '@bmai/licenses-api-client';

/**
 * Pure reassignment-recommendation logic — no I/O. Derives within-pool seat
 * recommendations from real inventory + seat-activity data (mirrors the Cost
 * forecast's {@link ../spend/cost/forecast/CostRecommendations}). Three kinds:
 *  - **reduce**: seats assigned but inactive (idle) — safe to reclaim. Only when
 *    seat-activity data is available (idle is otherwise unknown, not zero).
 *  - **elevate**: unassigned (free) seats already paid for — assign them before buying more.
 *  - **expand**: pools with no free and no idle seats — the only kind that implies procurement.
 */
export interface SeatRecommendationInput {
    provider: string;
    /** Idle (assigned-but-inactive) seats, or null when seat-activity data is unavailable. */
    idle: number | null;
    /** Unassigned (free) seats. */
    available: number;
    total: number;
}

export function reassignmentRecommendations(pools: SeatRecommendationInput[]): RecommendationPanelDto[] {
    const reduce: string[] = [];
    const elevate: string[] = [];
    const expand: string[] = [];

    for (const pool of pools) {
        if (pool.idle !== null && pool.idle > 0) {
            reduce.push(`${pool.provider}: ${pool.idle} idle seat${plural(pool.idle)} — assigned but inactive, safe to reclaim.`);
        }
        if (pool.available > 0) {
            elevate.push(`${pool.provider}: ${pool.available} unassigned seat${plural(pool.available)} ready to assign — use these before buying more.`);
        }
        if (pool.total > 0 && pool.available === 0 && (pool.idle === null || pool.idle === 0)) {
            expand.push(`${pool.provider}: fully allocated (no free or idle seats) — add capacity if demand grows.`);
        }
    }

    const panels: RecommendationPanelDto[] = [];
    if (reduce.length > 0) panels.push({ kind: 'reduce', title: 'Reclaim idle seats', items: reduce });
    if (elevate.length > 0) panels.push({ kind: 'elevate', title: 'Fill open seats first', items: elevate });
    if (expand.length > 0) panels.push({ kind: 'expand', title: 'Consider adding capacity', items: expand });
    return panels;
}

function plural(n: number): string {
    return n === 1 ? '' : 's';
}
